import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/components/ui/Icon';
import { WrapupInline, type DispositionOption, type TagOption } from './WrapupInline';
import { useSipStore } from '../stores/sipStore';
import { useCustomerCard } from '../stores/customerCard';
import { useWrapupSettings } from '../stores/wrapupSettings';
import { agentApi } from '@/shared/api/agentApi';

/**
 * Global call-summary host. Rendered once from AgentLayout, independent of the
 * page the agent is on. How it appears is set by the business admin
 * (tenant.features.agent_wrapup_mode → /agent/settings):
 *   - 'modal'   : full-screen blocking overlay; the call MUST be documented.
 *   - 'sidebar' : non-blocking drawer; the agent can keep working and document
 *                 when convenient (skip allowed).
 *   - 'off'     : not shown at all (no per-call documentation).
 */
export function WrapupOverlay() {
  const state = useSipStore((s) => s.state);
  const active = useSipStore((s) => s.active);
  const mode = useWrapupSettings((s) => s.mode);
  const [dispositions, setDispositions] = useState<DispositionOption[]>([]);
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [lastNumber, setLastNumber] = useState<string | null>(null);
  const [lastCallId, setLastCallId] = useState<number | null>(null);
  const [callerName, setCallerName] = useState<string | null>(null);

  // Remember the call identity while the call is live, so the summary (shown
  // after the call has ended, active=null) still has context.
  useEffect(() => {
    if (active?.number) setLastNumber(active.number);
    if (active?.callId) setLastCallId(active.callId);
  }, [active?.number, active?.callId]);

  // Stand down when the customer card is open (it hosts the wrap-up inline), OR
  // when the agent already filled the summary for this call during the call —
  // don't pop a duplicate wrap-up at hangup. Also stand down entirely when the
  // business disabled the wrap-up ('off'); in that case finishWrapup must still
  // run so the softphone returns to 'ready' (handled by the effect below).
  const cardOpen = useCustomerCard((s) => s.open);
  const isSummarized = useCustomerCard((s) => s.isSummarized);
  const alreadySummarized = isSummarized(active?.number ?? lastNumber);
  const inWrapup = state === 'wrap-up' && !cardOpen && !alreadySummarized;
  // מסך שמנהל את התיעוד בעצמו (מסך העבודה על קמפיין — פאנל "פעולות")
  // מדליק את אותו דגל שמכבה את כרטיס הלקוח הצף. בלעדיו, ניתוק פותח כאן
  // עוד חלון סיכום מעל מסך שכבר מציג אחד — שני טפסים לאותה שיחה.
  const suppress = useCustomerCard((s) => s.suppressFloating);
  const showing = inWrapup && mode !== 'off' && ! suppress;

  // 'off': there is no summary UI to leave wrap-up, so auto-advance back to
  // ready the moment we enter wrap-up. Keeps the state machine unstuck.
  useEffect(() => {
    if (inWrapup && mode === 'off') {
      useSipStore.getState().finishWrapup();
    }
  }, [inWrapup, mode]);

  useEffect(() => {
    if (!showing) return;
    void agentApi.callDispositions().then((d) => setDispositions(d as DispositionOption[])).catch(() => {});
    void agentApi.callTags().then((tg) => setTagOptions(tg)).catch(() => {});
  }, [showing]);

  useEffect(() => {
    if (!showing || !lastNumber) { setCallerName(null); return; }
    let alive = true;
    void agentApi.lookup(lastNumber).then((r) => { if (alive) setCallerName(r?.name ?? null); }).catch(() => {});
    return () => { alive = false; };
  }, [showing, lastNumber]);

  if (!showing) return null;

  async function saveWrapup(d: { disposition: string; notes: string; callback_at: string | null; tags: string[]; reason_code?: string | null }) {
    const payload = {
      disposition: d.disposition,
      notes: d.notes || undefined,
      callback_at: d.callback_at,
      tags: d.tags,
      // סיבה בתוך הסטטוס (2.8) — השרת דוחה ב-422 סטטוס עם סיבות שנשמר בלעדיה.
      reason_code: d.reason_code || undefined,
    };
    // Softphone (JsSIP) calls don't have a CDR id client-side — the Call row is
    // created server-side on hangup. Fall back to resolving by number so the
    // wrap-up (and any callback) is never silently dropped.
    if (lastCallId) {
      await agentApi.disposeCall(lastCallId, payload);
    } else if (lastNumber) {
      await agentApi.disposeLatestCall({ number: lastNumber, ...payload });
    } else {
      throw new Error('no call to document');
    }
  }

  const header = (
    <div style={{ padding: 'var(--pad-card)', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--accent-soft)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon name="phone" size={22} color="var(--accent)" />
      </div>
      <div>
        <div style={{ fontSize: 'var(--t-xl)', fontWeight: 700 }}>סיכום שיחה</div>
        <div style={{ fontSize: 'var(--t-sm)', color: 'var(--text-2)' }}>
          {callerName ? `${callerName} · ` : ''}
          <span dir="ltr">{lastNumber ?? ''}</span>
        </div>
      </div>
    </div>
  );

  const body = (
    <div style={{ padding: 'var(--pad-card)' }}>
      <WrapupInline
        dispositions={dispositions}
        tagOptions={tagOptions}
        onSave={saveWrapup}
        chrome={false}
        // אין "דלג" גם במגירה (06/10/2026) — כל שיחה מתועדת. המגירה רק לא
        // חוסמת את שאר המסך.
        draftKey={lastCallId ? `call:${lastCallId}` : lastNumber}
        phoneNumber={lastNumber}
      />
    </div>
  );

  // SIDEBAR: non-blocking drawer pinned to the inline-end edge. No scrim, so the
  // agent can keep interacting with the app and document when convenient.
  if (mode === 'sidebar') {
    return createPortal(
      <aside
        role="dialog"
        aria-label="סיכום שיחה"
        style={{
          position: 'fixed', insetBlock: 0, insetInlineEnd: 0, zIndex: 1150,
          width: 'min(420px, 100%)', display: 'flex', flexDirection: 'column',
          background: 'var(--surface)', borderInlineStart: '1px solid var(--line)',
          boxShadow: 'var(--shadow-3)', overflow: 'auto',
          animation: 'slidein-end 160ms ease',
        }}
      >
        {header}
        {body}
      </aside>,
      document.body
    );
  }

  // MODAL (default): full-screen blocking overlay; documentation required.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="סיכום שיחה"
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'oklch(0% 0 0 / 0.55)',
        display: 'grid', placeItems: 'center', padding: 'var(--gap)',
        animation: 'fadein 140ms ease',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 720, maxHeight: '92vh', overflow: 'auto',
          background: 'var(--surface)', borderRadius: 'var(--r-4)',
          border: '1px solid var(--line)', boxShadow: 'var(--shadow-3)',
        }}
      >
        {header}
        {body}
      </div>
    </div>,
    document.body
  );
}
